import { maskVendor } from "@/shared/lib/vendorMask";
import { errorToast } from "@/shared/lib/apiErrors";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  Crop,
  Film,
  Image as ImageIcon,
  Loader2,
  RefreshCw,
  Trash2,
  XCircle,
} from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { taskStatusBorderedBadgeClass, taskStatusLabel } from "@/shared/lib/taskStatus";
import { formatQueueStatus } from "@/shared/lib/queueInfo";
import { QueueHint } from "@/shared/ui/QueueHint";
import { Badge } from "@/shared/ui/badge";
import { sceneLabelOf } from "@/entities/scene-card/catalog";
import { longImageDisplayName, longImageSceneKeyOfName } from "@/entities/work/model/longImageName";
import { isPresetRatio, type PresetRatio } from "@/entities/scene-card/model/presets";
import { isUploadProvider, posterProviderLabel } from "../model/providerLabels";
import { toast } from "@/shared/hooks/use-toast";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { useEffect, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { displayableAssets, pickFullImage } from "../longimage/model/assetView";
import { LongImageGeneratingPanel } from "../longimage/ui/LongImageGeneratingPanel";
import { LongImageResultPanel } from "../longimage/ui/LongImageResultPanel";
import { LongImageSegmentsPanel, isScreenSegment } from "../longimage/ui/LongImageSegmentsPanel";
import { SegmentEditorDialog } from "../longimage/ui/SegmentEditorDialog";
import { hiddenBlockIdsOf, useLongImageSpec } from "../longimage/model/useLongImageSpec";
import { promptForRecreate } from "../model/free-create/recreatePrompt";
import { usePosterTaskPolling } from "../model/usePosterTaskPolling";
import { posterService, type PosterAssetDto } from "../api/posterService";
import { triggerDownload } from "@/shared/lib/download";
import { AssetEditor } from "../ui/components/editor/AssetEditor";
import { AnimateImageDialog, type AnimateImageTrigger } from "../ui/components/AnimateImageDialog";
import { GroupContinueBar } from "./components/detail/GroupContinueBar";
import { GeneratingPanel } from "./components/detail/GeneratingPanel";
import { GroupRedrawPanel } from "./components/detail/GroupRedrawPanel";
import { GroupCarouselPreview } from "./components/detail/GroupCarouselPreview";
import { groupContinueState } from "../model/groupContinue";
import { retryPosterTask } from "../model/retryQuote";
import { SKU, imageVariant } from "@/entities/pricing/model/priceBook";
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
import { acquireQuoteId, useQuotePreview } from "@/entities/pricing/model/useQuote";
import { longImageBlockRegenQuoteParams } from "../longimage/model/blockRegenQuote";
import { useProviderCapabilities } from "@/entities/media-provider/model/useProviderCapabilities";
import type { Tier } from "@/entities/media-provider/api";
import { PosterResultStrip } from "../ui/components/detail/PosterResultStrip";
import { TaskSettingsFold } from "../ui/components/detail/TaskSettingsFold";
import { detectShape, sizeSummary } from "../model/detailShape";
import { shortfallNote } from "../model/deliveredTier";
import { activeBlockAt, segmentOffsets } from "../longimage/model/segmentOffsets";
import {
  generationProgress,
  slotBySeq,
  slotCaption,
  slotStageBadge,
} from "../model/generationProgress";
import type { BrandPreview } from "../ui/components/PosterCanvas";
import { NominateToKbButton } from "@/entities/knowledge/ui/NominateToKbButton";

export default function PosterTaskDetailPage() {
  const { t } = useTranslation("poster");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // 2026-05-25 — 自由创作生成后跳来的(state.from=freecreate)→「返回」回自由创作页连续创作;
  // 否则(从工作台/历史进来)回工作台。
  const cameFromFreeCreate = (location.state as { from?: string } | null)?.from === "freecreate";
  const taskId = id ? Number(id) : null;
  const { task, assets, isPolling, timedOut, unavailable, refresh, restart } =
    usePosterTaskPolling(taskId);
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  // V26 (2026-05-14) — 编辑器 save/重画 pending 期间,grid 卡片的"下载"按钮要拦住,
  // 否则用户在合成 2-3s 窗口里点 grid 下载,会拿到旧 objectKey 的签名 URL → 下到原图。
  const [editorSavePending, setEditorSavePending] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  /**
   * 用户**主动**点过某一张没有(W3 修)。
   *
   * 等待态的主位规则是「用户没挑过 → 跟着最新落地的那张走;挑过 → 钉住不动」——
   * 后半句是纪律:不能把用户正在看的图从他眼皮底下换掉。
   * 但 `selectedAssetId` 有一条既有的**自动**兜底(进详情默认选中一张),它和用户的真实选择
   * 是两回事;把它当成「用户选过了」传下去,主位就永远钉在第一张出来的那张上,
   * 「跟着最新走」那条分支一次都不会触发 —— 状态走查截图里主位停在 #1 而 #2#3 已经出来了。
   */
  const [pickedByUser, setPickedByUser] = useState(false);
  // V26.4 (2026-05-14) — 移除 RegenerateModal:i2i / t2i 重画统一进 AssetEditor 三 tab
  // (AI 改图 / AI 重画),grid 卡片不再有"重新生成"快捷入口,只剩下载。

  // C3(2026-07-22)— 长图/横宽任务的资产集 = 整图(seq=1)+ 逐块分段(assetMeta.segment);
  // 分段是附属产物,归 LongImageSegmentsPanel 逐块面板管。详情页的「作品」只有整图 ——
  // 否则默认选中/网格/成败计数全被分段带偏(seq 最大 = 末段,用户进来第一眼看到的是
  // 136px 高的 CTA 窄条,整图反被排到末尾;计数也变成「5/1」)。
  // 后端列表页封面/计数早已按同口径排除分段(PosterService.isLongImageSegment),此处对齐。
  const displayAssets = useMemo(
    () => displayableAssets(assets, task?.posterKind === "LONG_IMAGE"),
    [assets, task?.posterKind]
  );

  // 2026-05-26 — 进详情默认选中最新的图(seq 最大),跟 grid「新图排前面」一致,
  // 不再默认显示原图(seq 最小那张)。
  useEffect(() => {
    if (selectedAssetId != null) return;
    const latest = pickFullImage(displayAssets);
    if (latest) setSelectedAssetId(latest.id);
  }, [displayAssets, selectedAssetId]);

  // 模版库 — 编辑器要据此渲染 anchors
  const templatesQ = useQuery({
    queryKey: ["poster-templates"],
    queryFn: posterService.listTemplates,
    staleTime: 5 * 60_000,
    enabled: task != null,
  });

  // 编辑器需要的"用户素材"占位 — v2.9 MVP detail 编辑器只调位置/大小,
  // s15.6 (2026-05-08): 用 task.logoUrl / task.qrCodeUrl (后端 GET /tasks/{id} 单端点注入的
  // presigned URL) 让 canvas 真渲染用户上传的 logo / QR, 不再用占位阴影区。
  const editorBrand: BrandPreview = useMemo(
    () => ({
      logoUrl: task?.logoUrl ?? null,
      qrcodeUrl: task?.qrCodeUrl ?? null,
      contactLines: [],
    }),
    [task?.logoUrl, task?.qrCodeUrl]
  );

  // 1005:GPT 线路上游有时不认 size(2K 单交 1086×1448)—— 页头的「2K」只说请求档,
  // 选中这张实交低于请求档时,把实际像素与规格如实写出来(规则与 ai 计费同一条)。
  const selectedAsset = useMemo(
    () => displayAssets.find((a) => a.id === selectedAssetId) ?? null,
    [displayAssets, selectedAssetId]
  );
  const deliveredNote = selectedAsset
    ? shortfallNote(
        task?.resolutionTier,
        null,
        selectedAsset.actualWidth,
        selectedAsset.actualHeight
      )
    : null;

  async function handleRetry() {
    if (taskId == null) return;
    setBusy(true);
    try {
      // ★修复波 0926(代理 H):重试是真出图真扣钱,先取正式报价单再重试
      //   (取单失败不阻断,见 retryPosterTask)。
      await retryPosterTask(taskId, task ?? null);
      // ★restart 而不是只弹 toast(0816 真机三次复现):PARTIAL 在轮询钩子里是终态,
      //   doneRef 已置位;重试把任务打回 RUNNING 后没人再拉 —— 后台 5/5 出齐,
      //   页面停在旧的「部分成功」横幅。SegmentEditorDialog/regen 各处都记得调
      //   restart,唯独这个按钮漏了(函数对≠接线对,第六次)。
      restart();
      toast({ title: t("toast.retryTriggered") });
    } catch (e: unknown) {
      toast(errorToast(e, { fallbackTitle: t("toast.retryFailed") }));
    } finally {
      setBusy(false);
    }
  }

  async function handleCancel() {
    if (taskId == null) return;
    const ok = await confirm({
      title: "取消生成?",
      description: "取消后本次任务终止(已生成的图保留),可用「用此提示词重新创作」重新发起。",
      confirmText: "取消任务",
      variant: "destructive",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await posterService.cancelTask(taskId);
      toast({ title: "任务已取消" });
      refresh();
    } catch (e: unknown) {
      toast(errorToast(e, { fallbackTitle: "取消失败" }));
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (taskId == null) return;
    const ok = await confirm({
      title: t("taskDetail.deleteConfirmTitle"),
      description: t("taskDetail.deleteConfirmDesc"),
      confirmText: t("taskDetail.deleteConfirmBtn"),
      variant: "destructive",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await posterService.deleteTask(taskId);
      toast({ title: t("toast.taskDeleted") });
      navigate("/ai-haibao");
    } catch (e: unknown) {
      toast(errorToast(e, { fallbackTitle: t("toast.deleteFailed") }));
    } finally {
      setBusy(false);
    }
  }

  const isFailed = task?.status === "FAILED";
  const isPartial = task?.status === "PARTIAL";
  const isLongImage = task?.posterKind === "LONG_IMAGE";
  /**
   * W2 D2 — 长图的成败在**区块**上,不在「产物」上。
   *
   * 长图的产物只有整图一张(分段是附属),successCount/failedCount 恒 1/0 → 页头写
   * 「已完成 · 1/1 成功」,而 8 个区块里挂了 3 个。后端现在把 ai 交付整图时写的那份区块数
   * (blockCount / failedBlockCount)透出来了,页头和 PARTIAL 横幅都改读它。
   * 存量长图(整图 assetMeta 没写 blockCount)→ null,回落原产物口径,不显示错的数。
   */
  const blockTally =
    isLongImage && task?.blockCount != null && task.blockCount > 0
      ? { total: task.blockCount, failed: task.failedBlockCount ?? 0 }
      : null;
  // C3:整图(SUCCESS,seq 最大 —— 整图重出时 seq 会 +1)。有它就走整图面板。
  const longImageFull = useMemo(
    () => (isLongImage ? pickFullImage(displayAssets) : null),
    [isLongImage, displayAssets]
  );

  // 2026-07-28 W2 — 形态判定收口到纯函数(单图/组图/套图/长图/宽图)
  const shape = detectShape(task, displayAssets);
  const [regenBusy, setRegenBusy] = useState(false);

  /** 换衣单张重出:后端只扣一张的钱;失败时那一格的旧图原样留着(所以这里只提示,不清空)。 */
  const handleTryonRegen = async (seq: number) => {
    if (!task) return;
    setRegenBusy(true);
    try {
      await posterService.regenerateTryonPose(task.id, seq);
      toast({ title: `第 ${seq} 张已重新出图`, description: "出好会自动刷新" });
      restart();
    } catch (e) {
      toast({
        title: "重出没发起来",
        description: errorToast(e, { hiddenDescription: "重试一次" }).description,
        variant: "destructive",
      });
    } finally {
      setRegenBusy(false);
    }
  };

  /* ── 长图「左看右改」的两根线(2026-08-07)──────────────────────────────
     ① 点右边某块 → 左边跳过去(jump,带 nonce 让同一块能连点)
     ② 左边滚到哪 → 右边那块高亮(ratio → activeSegmentId)
     位置由分段高度累加算出;算不准(和整图高对不上)时 offsets 为 null,
     ①的入口直接不给、②不高亮 —— 跳错位比不能跳更糟。 */
  const [longImageJump, setLongImageJump] = useState<{ ratio: number; nonce: number } | null>(null);
  const [longImageRatio, setLongImageRatio] = useState(0);
  /**
   * 正在编辑的那一段(2026-08-08:块级编辑器 = 长图版的「详情页操作」)。
   *
   * ★**整图品牌入口已砍掉**:它只能选四角锚点、不吃 bbox,logo 比例一怪效果就差,
   *   而块级那条路本来就吃 bbox(DragOverlayCanvas 归一化坐标 → ai /compose)。
   *   同一件事留两个入口、其中一个还更弱,只会让人先试那个弱的。
   *   ai / api 侧的品牌叠加**保留不动** —— 存量已挂 logo 的任务重拼时还得把品牌层带回来。
   */
  const [adjusting, setAdjusting] = useState<PosterAssetDto | null>(null);
  /** LI30 整图级挂标弹窗(0910)。 */
  /**
   * 这一稿真正发给引擎的内容(块的 props)——「改内容」渲表单、「回搭建器」回填都用它。
   * 只在长图形态拉;拿不到就降级成「只能改画面」,不报错。
   */
  const longImageSpec = useLongImageSpec(taskId ?? null, shape === "LONG_IMAGE");
  /**
   * 修复波 0926(代理 H):「改这一块」的 AI 改图 / AI 重画 按单块计一笔出图 —— 按钮上亮价。
   * 只在打开的是**图块**时报(文字块没有这两个 tab,报了也没地方显示);口径见 blockRegenQuote。
   */
  const adjustingIsImageBlock =
    adjusting?.blockId != null &&
    longImageSpec.blockById.get(adjusting.blockId)?.primitive === "image";
  const blockRegenQuote = useQuotePreview(
    adjustingIsImageBlock ? longImageBlockRegenQuoteParams(longImageSpec.raw) : null
  );
  /** E-09b:已删块集合 —— 跳转定位与分段列表都要排除它们(hidden 真相在 payload)。 */
  const longImageHiddenIds = useMemo(
    () => hiddenBlockIdsOf(longImageSpec.raw),
    [longImageSpec.raw]
  );
  const longImageOffsets = useMemo(
    () =>
      shape === "LONG_IMAGE"
        ? segmentOffsets(
            longImageFull?.actualHeight ?? null,
            /* ★取 `assets` 不是 `displayAssets` —— 后者对长图**恒定过滤掉分段**
               (assetView.displayableAssets:「分段是附属产物,不是独立作品」)。
               拿它算位置永远是空集 → offsets 恒 null → 跳转入口永远不出现。
               真页面上一眼就看出来了(右侧块名点不动),单测里看不出来:
               我喂给 segmentOffsets 的是手搭的数组,它当然有分段。 */
            assets
              .filter(
                (a) =>
                  a.segment &&
                  a.blockId &&
                  /* E-09b:删掉(hidden)的块已不在成品图里,它的分段再参与算高度,
                     后面每块的跳转位置都整体偏移。hidden 真相在任务 payload 里。 */
                  !longImageHiddenIds.has(a.blockId)
              )
              .sort((a, b) => a.seq - b.seq)
          )
        : null,
    [shape, longImageFull, assets, longImageHiddenIds]
  );
  const activeSegmentId = activeBlockAt(longImageOffsets, longImageRatio);
  // 成品条按 seq 升序(第 1 张在最左,和爆点①②③ / 比例顺序对得上);
  // ★注意与老网格「新图排前面」不同 —— 组图/套图的顺序本身有语义,倒着排会让
  //   「① 轻便」跑到最右边,用户对不上号。
  const stripAssets = useMemo(
    () => [...displayAssets].sort((a, b) => a.seq - b.seq),
    [displayAssets]
  );
  /**
   * W1(2026-08-02)—— 槽位表接管「还差哪几张、各自什么画幅」。
   *
   * 老 `pendingRatios(task, 已落地数)` 按「已落地的一定是前 N 个 seq」推缺哪一张,
   * 只要有一张先失败或后到就取错画幅(单测坐实:seq1 成功 + seq3 失败时,缺的是 seq2 的
   * 1:1,它给出 16:9)—— 占位框按错画幅撑开,图回来时宽度突变,正好制造了骨架本想避免的跳动。
   * 槽位按 seq 自己取,与到货顺序无关。
   */
  const progress = generationProgress(task, displayAssets, unavailable);
  /**
   * W2(2026-08-02)—— 等待态是**一等页面状态**,不是「成品页减内容」。
   *
   * ★长图绕行:它的成败在区块上不在产物上(整图恒 1 张),槽位模型对它没有意义,
   *   仍走 LongImageResultPanel / LongImageSegmentsPanel 那条既有路径。
   */
  const isGenerating =
    !isLongImage && (progress.phase === "QUEUED" || progress.phase === "STREAMING");
  /**
   * 「此刻在等」的全形态口径(2026-08-03)。isGenerating 因槽位模型绕行恒 false 于长图,
   * 于是「等待态不摆点不动的操作」那排闸(一键成片/套图)对长图等待期整体失效 ——
   * 等待页截图实锤:RUNNING 的长图头上还摆着「一键成片」。操作矩阵的显隐用这个,
   * 槽位相关的渲染仍用 isGenerating。
   */
  const isWaiting = isGenerating || task?.status === "PENDING" || task?.status === "RUNNING";
  /**
   * 卡底副标题 / 主画面角标都从**槽位表**派生(收口,2026-08-02)。
   * 改前 `captionOf(shape, task, asset)` 与 `stageBadge(shape, task, asset)` 各自
   * 拿 (形态, task) 把身份再算一遍 —— 同一件事三处口径,改一处漏两处。
   */
  const slotOf = slotBySeq(progress.slots);

  async function handleAssetDownload(a: (typeof displayAssets)[number]) {
    if (editorSavePending) {
      toast({ title: t("toast.syntheszing"), description: t("toast.synthesizingDesc") });
      return;
    }
    try {
      const url = await posterService.downloadAsset(a.id);
      // P-B:长图产物是 JPG,按 DTO mime 派生扩展名(历史写死 .png)
      const ext = a.mime === "image/jpeg" ? "jpg" : "png";
      triggerDownload(url, `poster-${a.taskId}-${a.seq}.${ext}`);
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "下载失败" }));
    }
  }

  /**
   * B2-1/B2-2 组图分步:还差几张 / 现在能不能续。
   * ★张数与费用口径都来自台本(`groupPointTitles`),不在这里另算一份 ——
   *   另算必漂,而漂的后果是用户按这个数付钱。
   */
  /**
   * 这个任务**每张真实积分** —— 续出条与重出面板共用一处。
   *
   * ★真源是**后端价目表**(`/pricing/catalog`),按 provider×档位查规格档 —— 与结算按单兑现
   *   的是同一个价源。此前这里是 `capabilities.tierCredits ?? 前端常量表`:那条兜底
   *   「只认档位、不认 provider」(同为 1K,gpt-image 280 而万相 160),真跑对账坐实过
   *   续出 2 张显示 560、实扣 320。现在兜底整条去掉 —— **取不到就不显示数字**,
   *   而不是退回一个只认一半维度的估算。
   */
  const { book: priceBook } = usePriceBook();
  const creditsPerImage = useMemo(() => {
    const tier = (task?.resolutionTier ?? "2K") as Tier;
    return task?.provider
      ? priceBook.unit(SKU.posterImage, imageVariant(task.provider, tier))
      : null;
  }, [priceBook, task?.provider, task?.resolutionTier]);

  const continueState = groupContinueState({
    groupPointTitles: task?.groupPointTitles,
    status: task?.status,
    assets: displayAssets,
    creditsPerImage,
  });
  /**
   * 这一单的报价参数(与上面 `creditsPerImage` 同一个 provider × 档位口径)。
   *
   * ★ 用同一处派生,不各写一份:续出条上显示的数和真正落单的那张单必须同源,
   *   否则又回到「看的是 A、扣的是 B」。
   */
  const quoteParamsForImages = (qty: number) => {
    const tier = (task?.resolutionTier ?? "2K") as Tier;
    return task?.provider && qty > 0
      ? { skuCode: SKU.posterImage, variantKey: imageVariant(task.provider, tier), qty }
      : null;
  };

  const [continuing, setContinuing] = useState(false);
  async function handleContinueGroup() {
    if (taskId == null || continuing) return;
    setContinuing(true);
    try {
      // ★ 批 L:续出是真出图真扣钱,提交这一刻落一张正式报价单,单号带给后端绑到
      //   `{taskId}:group:{dispatchId}`。张数 = 还差几张(与按钮上写的那个数同源)。
      //   报价失败不阻断(返 null)—— 把「没有报价单」修成「不能续出」,后者代价更大;
      //   没有单的这一单会在后端落 `poster_group_without_quote` WARN 并被验收判据逮到。
      const quoteId = await acquireQuoteId(quoteParamsForImages(continueState.remaining));
      await posterService.continueGroup(taskId, quoteId);
      toast({ title: "开始出剩下几张", description: "会照着封面的版式出,稍等一下" });
      // ★ restart 而不是 refresh:任务刚被打回 PENDING,要把轮询重新拉起来
      //   (refresh 只取一次,拿到 PENDING 就停了 —— 用户会盯着一个不动的页面)
      restart();
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "续出失败" }));
    } finally {
      setContinuing(false);
    }
  }

  const [carouselOpen, setCarouselOpen] = useState(false);
  // I2V-PC-1:「让这张图动起来」轻确认层(null=不弹;值即冻结的 (taskId, assetId))
  const [animateTrigger, setAnimateTrigger] = useState<AnimateImageTrigger | null>(null);
  const [redrawing, setRedrawing] = useState(false);
  async function handleRedraw(seq: number, title: string, detail?: string) {
    if (taskId == null || redrawing) return;
    setRedrawing(true);
    try {
      // 单张重出:一张的钱
      const quoteId = await acquireQuoteId(quoteParamsForImages(1));
      await posterService.redrawGroupImage(taskId, seq, title, detail, quoteId);
      toast({ title: "开始重出这一张", description: "会照着封面的版式出,稍等一下" });
      restart();
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "重出失败" }));
    } finally {
      setRedrawing(false);
    }
  }

  const resultStrip = (
    <div className="space-y-2.5">
      {/* B2-2:续出条紧跟成品条 —— 决定「要不要继续」的依据就是上面那张真封面。
          放在 resultStrip 内部而不是页面两处:详情页有「有可编辑选中图 / 没有」两条
          渲染路径,挂一处才不会漏。 */}
      <PosterResultStrip
        assets={stripAssets}
        selectedId={selectedAssetId}
        onSelect={setSelectedAssetId}
        captionOf={(a) => slotCaption(slotOf.get(a.seq), a)}
        busy={editorSavePending}
        onDownload={handleAssetDownload}
      />
      {/* ★等待态不挂续出条:它是**终态的决策条**(「照这张出剩下 N 张」),
          出图途中挂出来只会写「封面还没出好 / 正在出图」—— 一句此刻无法据以决策的话。
          打包下载同理:零产物时点了拿空包。 */}
      {/* 换衣:一组动作里某张不满意,只重出这一张(只花一张的钱)。
          ★与组图重出分开写:组图那条要改爆点文案、要提醒「换封面其余几张失去参照」,
            换衣没有这两件事 —— 底图是选定的动作,重出就是同一张再来一次。 */}
      {task?.posterKind === "TRYON" && !isGenerating && selectedAsset?.status === "SUCCESS" && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/30 px-4 py-3">
          <span className="text-sm">第 {selectedAsset.seq} 张不满意?只重出这一张,其余不动。</span>
          <span className="text-xs text-muted-foreground">
            {(() => {
              const c = priceBook.unit(SKU.tryon, "default");
              return c == null ? "重出失败不会动现在这张" : `约 ${c} 积分 · 重出失败不会动现在这张`;
            })()}
          </span>
          <Button
            size="sm"
            variant="outline"
            data-tryon-regen={selectedAsset.seq}
            disabled={regenBusy}
            onClick={() => void handleTryonRegen(selectedAsset.seq)}
          >
            {regenBusy ? "重出中…" : "重出这一张"}
          </Button>
        </div>
      )}

      {shape === "GROUP" && !isGenerating && (
        <>
          <GroupContinueBar
            state={continueState}
            onContinue={handleContinueGroup}
            busy={continuing}
          />
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {/* B2-4:唯一能验收「像不像一套」的视图 —— 148px 的小图条看不出版式差异 */}
            {stripAssets.some((a) => a.status === "SUCCESS") && (
              <button
                type="button"
                onClick={() => setCarouselOpen(true)}
                className="text-[12.5px] font-semibold text-primary underline-offset-4 hover:underline"
              >
                按轮播翻一遍 ▸
              </button>
            )}
            {/* B2-5:按序打包(复用既有 batch-download;组图一个 task 内多张)。
                ★与轮播同一道零产物闸:上面头注承诺的「零产物时点了拿空包」此前只靠
                  !isGenerating 挡,而 SUCCESS 即非 generating —— 0 张成品照样能点出空包。 */}
            {stripAssets.some((a) => a.status === "SUCCESS") && (
              <button
                type="button"
                onClick={() =>
                  void posterService
                    .batchDownload([taskId!], `爆点组图-${taskId}`)
                    .catch((e) => toast(errorToast(e, { fallbackTitle: "打包下载失败" })))
                }
                className="text-[12.5px] text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              >
                整组打包下载
              </button>
            )}
            {/* B2-3:改这条爆点、只重出这一张(★锚封面) */}
            {selectedAsset && (
              <GroupRedrawPanel
                seq={selectedAsset.seq}
                isCover={selectedAsset.seq === 1}
                initialTitle={task?.groupPointTitles?.[selectedAsset.seq - 1] ?? ""}
                otherCount={Math.max(stripAssets.length - 1, 0)}
                /* 与续出条同一个数(★假单价是对客红线,不能在这里自己再算一份) */
                credits={creditsPerImage}
                busy={redrawing}
                onRedraw={(title, detail) => void handleRedraw(selectedAsset.seq, title, detail)}
              />
            )}
          </div>
        </>
      )}
      {/* 全渠道套图与爆点组图对齐的打包口(2026-08-12 反馈:套图详情页少了「打包下载」
          那一栏)。多画幅套图本来就是「一次拿一组」的产物,逐张右键另存是体力活。
          守 GROUP 同两道闸:等待态不渲 + 零成品不渲(SUCCESS≠有成品,0 张点出空包)。 */}
      {shape === "RATIO_SET" &&
        !isGenerating &&
        stripAssets.some((a) => a.status === "SUCCESS") && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <button
              type="button"
              onClick={() =>
                void posterService
                  .batchDownload([taskId!], `全渠道套图-${taskId}`)
                  .catch((e) => toast(errorToast(e, { fallbackTitle: "打包下载失败" })))
              }
              className="text-[12.5px] text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              整组打包下载
            </button>
          </div>
        )}
    </div>
  );

  /**
   * 返回创建页时**带上这条任务的形态**(测试反馈 D-03,琳琳 8·10):
   *   「做完全渠道套图,继续做第二张,点击返回自由创作,跳转的是商品主图而不是全渠道套图」
   *
   * 根因:三种形态(商品主图 / 爆点组图 / 全渠道套图)route 完全相同,只差 `?scene=`
   * (见 PosterModeBar 头注:`poster_single` / `poster_group` / `poster_multiratio`)。
   * 这里回跳时把 scene 丢了,于是永远落回默认的商品主图 —— 用户得自己重新找一遍入口。
   *
   * `?scene=` 是 PosterCreatePage 认的"我是谁"(它专门做过"活过刷新"的处理,
   * 见那边的承重墙注释),所以带上它就够,不需要另造一套回跳协议。
   * 历史任务 sceneKey 为 null → 不带参数,行为与改前逐字一致。
   */
  /**
   * 这一单是从哪张场景卡进来的(E-02)。历史任务 sceneKey 为 null → 不显示。
   * ★1006 复测:一键长图由 api 建单不带 sceneKey,用途只在名字的用途段里 —— 与作品卡同口径
   *   (`mapPoster`)从名字解析出用途卡,标签显示「企业介绍长图」,标题只留「主题·时间」。
   */
  const sceneOriginTitle = task
    ? sceneLabelOf(task.sceneKey ?? longImageSceneKeyOfName(task.name))
    : null;

  const backToCreateHref = task?.sceneKey
    ? `/ai-haibao/new?scene=${encodeURIComponent(task.sceneKey)}`
    : "/ai-haibao/new";

  const backButton = (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-2 gap-1 text-muted-foreground hover:text-foreground"
      onClick={() => navigate(cameFromFreeCreate ? backToCreateHref : "/ai-haibao")}
    >
      <ArrowLeft className="h-4 w-4" />
      {cameFromFreeCreate ? t("taskDetail.backToCreate") : t("taskDetail.back")}
    </Button>
  );

  /**
   * D9(2026-07-30)— 任务不可访问的终态空态。
   *
   * 改前:后端对「已删除」和「越权」统一返 404(不泄漏存在性),而轮询把 404 当网络抖动,
   * 每 5s 重试到 5 分钟超时 —— 这段时间里页面一直是「加载中…」。W1 走查里
   * TENANT_STAFF 打开别人的任务就卡在这,读起来像系统坏了,而不是「你看不到这条」。
   *
   * 这里必须 early return 整页,不能只挂一条横幅:页头/操作区/编辑器全部依赖 task,
   * task 恒 null 时它们是一排禁用按钮 + 空网格,留在页面上只会让人以为还能等到东西。
   */
  if (unavailable) {
    return (
      <div className="space-y-4">
        {backButton}
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-3 px-6 py-16 text-center">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <AlertCircle className="h-5.5 w-5.5" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold">打不开这个作品</p>
              {/* 后端故意不区分「不存在」和「没权限」,前端也不猜 —— 把两种可能都说出来,
                  比编一个确定的原因诚实 */}
              <p className="max-w-md text-xs leading-relaxed text-muted-foreground">
                任务 {taskId} 已被删除,或者不在你的可见范围内(员工只能看自己创建的作品,
                管理员可见全租户)。
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="gap-1.5" onClick={restart}>
                <RefreshCw className="h-3.5 w-3.5" />
                重试
              </Button>
              <Button
                size="sm"
                onClick={() => navigate(cameFromFreeCreate ? "/ai-haibao/new" : "/ai-haibao")}
              >
                {cameFromFreeCreate ? t("taskDetail.backToCreate") : t("taskDetail.back")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  /**
   * 首帧加载态(2026-09-10 老板实反「长图生成中页面加载混乱」)。
   *
   * task 第一拍还没拉回来时(每次进详情页都有这一个 RTT 的窗口,点「生成」跳过来尤其明显),
   * 若落进下面的正式渲染,`detectShape(null)` 会默认判成「单图」、`generationProgress(null)`
   * 又给出空的 QUEUED 态 → 主台先闪一张通用等待面板(那张自相矛盾的「0/0 已出图 · 全部出完了」
   * 空卡),等 task 到货认出是长图/组图再**整块 reflow** 成真实版式。用户看到的就是「加载混乱」。
   *
   * 这里 early return 一个占住同一块主台高度的中性加载态:盒尺寸预留 → task 到货只是原地填内容,
   * 没有版式跳动;也不再按 null 猜形态。超时(getTask 一直失败到 5min)给明确的重试出口。
   */
  if (task == null) {
    return (
      <div className="space-y-4">
        {backButton}
        {/* 加载态只保留一处「正在拉取」的话术(在中央盒子里);页头不再重复第二句
            loading 副标题(设计审查:三处「加载中」措辞叠在一起,收成一句)。 */}
        <header className="flex min-w-0 items-center gap-2.5">
          <div className="rounded-lg bg-primary/10 p-2">
            <ImageIcon className="h-4.5 w-4.5 text-primary" />
          </div>
          <h1 className="truncate text-lg font-bold text-foreground">
            {t("taskDetail.titleLoading")}
          </h1>
        </header>
        <div className="flex items-center justify-center rounded-xl border border-dashed border-border/60 bg-muted/20 lg:h-[calc(100dvh-13rem)] lg:min-h-[30rem]">
          {timedOut ? (
            <div className="flex flex-col items-center gap-2 text-center text-muted-foreground">
              <AlertCircle className="h-5 w-5 text-warning" />
              <span className="text-sm">这条作品暂时没拉回来</span>
              <Button variant="outline" size="sm" className="mt-1 gap-1.5" onClick={restart}>
                <RefreshCw className="h-3.5 w-3.5" />
                重试
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">正在拉取作品…</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {backButton}

      {/* ── 页头:压成一行 ────────────────────────────────────────────────────
          W2 前这里是「icon-box + 两行标题」+ 下面一整张状态条 Card + 再下面一整张
          「本次设置」Card,三块加起来 ~550px,把图挤到第二屏(用户原话「头轻脚重」)。
          现在状态 badge、成败计数、引擎、尺寸全部并进标题行,时间进折叠区。 */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="rounded-lg bg-primary/10 p-2">
            <ImageIcon className="h-4.5 w-4.5 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {/* 0829 走查 P1:标题此前恒是「视觉舱 · 任务 #<19 位 id>」,回头看认不出这单是什么。
                  任务自带 `name`(作品库列表显示的就是它),优先用它;没有(或空白)才退回 id 版。 */}
              <h1 className="truncate text-lg font-bold text-foreground">
                {task
                  ? longImageDisplayName(task.name) || t("taskDetail.title", { id: task.id })
                  : t("taskDetail.titleLoading")}
              </h1>
              {/* ★只留一个转圈:改前 badge 里一个 + 旁边 isPolling 又一个,说的是同一件事 */}
              {task && (
                <StatusBadge
                  status={task.status}
                  label={
                    // 1104:排队单有 queueInfo →「排队中 · 前面 N 单 · 约 X 分钟」;否则原口径不变
                    formatQueueStatus(task.status, task.queueInfo)?.label ??
                    (isGenerating ? "正在出图" : undefined)
                  }
                />
              )}
              {/* 来源模块(测试反馈 E-02,爱民 8·10):「我在哪个按钮能看到我此次出图是点的哪个模块的图」。
                  三种形态 route 完全相同、任务标题只有一串 id,出完图回头看根本认不出这单是从哪张卡进来的。
                  词面取 SCENE_CARDS 的 title,不另写一份 —— 卡墙上叫什么,这里就叫什么。 */}
              {sceneOriginTitle && (
                <Badge variant="secondary" className="shrink-0 text-2xs font-normal">
                  {sceneOriginTitle}
                </Badge>
              )}
            </div>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              {/* ★等待态不说「成功」——「0/6 成功」读起来像六张全废。「成功」是终态词。 */}
              {isGenerating && task ? (
                <>
                  {progress.done === 0
                    ? `正在出 ${progress.total} 张`
                    : `${progress.done}/${progress.total} 已出图`}
                  {progress.failed > 0 && (
                    <span className="text-destructive"> · {progress.failed} 张没出来</span>
                  )}
                  {` · ${posterProviderLabel(task.provider)} · ${sizeSummary(task)}`}
                </>
              ) : task && isUploadProvider(task.provider) ? (
                // P1-4(0829 雷雷):上传素材不摆「1/1 成功 · AI 出图」——没出过图,计数与引擎名都是假象
                t("taskDetail.summaryUpload")
              ) : task ? (
                t(blockTally ? "taskDetail.summaryBlocks" : "taskDetail.summary", {
                  ok: blockTally ? blockTally.total - blockTally.failed : task.successCount,
                  total: blockTally ? blockTally.total : task.requestedCount,
                  fail: blockTally
                    ? blockTally.failed > 0
                      ? t("taskDetail.summaryFailBlocks", { n: blockTally.failed })
                      : ""
                    : task.failedCount > 0
                      ? t("taskDetail.summaryFail", { n: task.failedCount })
                      : "",
                  provider: posterProviderLabel(task.provider),
                  // W5:套图列全部比例(老写法只显示 task.ratio 那一个)
                  size: sizeSummary(task),
                }) + (deliveredNote ? ` · ${deliveredNote}` : "")
              ) : (
                t("taskDetail.fetching")
              )}
            </p>
            {task && (
              <QueueHint status={task.status} queueInfo={task.queueInfo} className="mt-0.5" />
            )}
          </div>
        </div>
        {/* ★W2 操作矩阵:此刻点不动的东西不该在此刻出现。
            等待态只留「取消生成 + 删除」——
              · 一键成片:改前 `disabled={busy || !task}` 是**可点**的,带走 `assetSeq ?? 1`,
                而零产物时那个 seq 还不存在;
              · 出全渠道套图 / 这个好·入库:本来就依赖 selectedAsset,灰着也是噪音;
              · 整组打包下载:改前组图态恒渲染,等待时点了拿空包。
            灰按钮不是「更保守」——它占位置、要解释、还骗点击。 */}
        <div className="flex shrink-0 items-center gap-2">
          {/* I2V-PC-1(2026-08-30)拆两颗 → 0901 用户拍板**收敛成一颗**:图上只留
              「让这张图动起来」(原地弹轻确认层,单镜直发不编分镜);多镜编排入口
              (原「据这张图编一部片」)挪进弹层底部当升级链接 —— fromPoster 编排流
              没删(AnimateImageDialog 内的文字链是它现在唯一的原生入口),只是不再
              与主推按钮并排打架。payload 对齐军师 make_image_video;后端派发前
              现签首帧,前端不碰 URL。 */}
          {/* 1016 V4 分裂按钮:主按钮仍是最常用的「让这张图动起来」(原地弹轻确认层,单镜 i2v);
              右侧下拉列出其余转视频方式(多镜编排「据这张图编一部片」)—— 此前它只能先开弹层
              再点底部文字链,路径绕。弹层里的文字链保留(两个入口指向同一处)。 */}
          {!isWaiting && (
            <div className="inline-flex items-stretch" data-testid="poster-animate-split">
              <Button
                variant="outline"
                size="sm"
                // ★前置条件先于报价(ADV-EDIT-TARGET-1 同课):没选中产物就没有 assetId,
                //   别弹「确认做吗」—— 弹层需要的稳定 ref 就是 (taskId, assetId)。
                disabled={busy || !task || !selectedAsset}
                onClick={() =>
                  task &&
                  selectedAsset &&
                  setAnimateTrigger({
                    taskId: task.id,
                    assetId: selectedAsset.id,
                    // BP-03 口径:带走**选中那张**的 seq(弹层里的编排升级链接要用)
                    assetSeq: selectedAsset.seq,
                    thumbUrl: selectedAsset.thumbUrl ?? selectedAsset.baseUrl,
                  })
                }
                className="rounded-r-none border-primary/40 text-primary hover:bg-primary/10"
                title="按这张图原样出一条短动效视频(单镜图生视频,画幅跟图走,不编分镜);要多镜头成片,点右侧箭头"
              >
                <Film className="mr-1 h-3 w-3" />
                让这张图动起来
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy || !task || !selectedAsset}
                    aria-label="更多转视频方式"
                    data-testid="poster-animate-more"
                    className="-ml-px rounded-l-none border-primary/40 px-1.5 text-primary hover:bg-primary/10"
                  >
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuItem
                    data-testid="poster-animate-orchestrate"
                    onSelect={() =>
                      task &&
                      selectedAsset &&
                      navigate(
                        `/ai-video/create?fromPoster=${task.id}&assetSeq=${selectedAsset.seq}`
                      )
                    }
                    className="flex flex-col items-start gap-0.5"
                  >
                    <span className="text-[13px]">据这张图编一部片</span>
                    <span className="text-[11.5px] text-muted-foreground">
                      多镜头分镜、按故事编排的完整成片
                    </span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
          {/* T4(2026-07-29)—— 从已出好的图直连全渠道套图。
              ★这条路此前等于没有:套图的两个入口(卡墙的卡、⚙参数里的三选一)都在
                「从零开始」那一侧,而真实高频场景是**主图已经出好了,现在要发抖音/小红书/
                朋友圈**。详情页原来只有「一键成片」和「基于此再创作」,后者是重新 T2I,
                产品会被重画。
              ★只给单图/组图:长图和宽图换比例没有意义(长图的比例是拼出来的)。 */}
          {!isWaiting && task && (shape === "SINGLE" || shape === "GROUP") && (
            <Button
              variant="outline"
              size="sm"
              disabled={busy || !selectedAsset?.baseUrl}
              onClick={() =>
                selectedAsset?.baseUrl &&
                navigate("/ai-haibao/new", {
                  state: {
                    ratioSetFrom: {
                      taskId: task.id,
                      assetSeq: selectedAsset.seq,
                      // ★2026-07-30(任务 21):带上 assetId —— 新路是「后端在存储内部复制」,
                      //   浏览器不再下载 4K 原图(那条路被页面 CSP connect-src 'self' 拦死,
                      //   用户看到的就是 Failed to fetch)。baseUrl 保留只作兜底与预览。
                      assetId: selectedAsset.id,
                      // ★必须是 baseUrl(无 overlay 的原图),不能用 thumbUrl/成品图:
                      //   成品上已经合成了 AIGC 角标与 LOGO/二维码,拿它当 i2i 底图会被
                      //   当成画面内容重绘 —— 角标糊掉、二维码扫不出。品牌叠加在新任务里
                      //   走「③ 叠什么」重新叠。
                      baseUrl: selectedAsset.baseUrl,
                      // ★回填用户原文,不是拼装串(2026-09-01):rawPrompt 里带着提交时
                      //   追加的【画幅构图】等段,灌回文案框再提交会把追加段再叠一层。
                      prompt: promptForRecreate(task),
                    },
                  },
                })
              }
              title="以这张图为底,一次出抖音/小红书/朋友圈等多个画幅(产品保持不变)"
            >
              <Crop className="mr-1 h-3 w-3" />
              出全渠道套图
            </Button>
          )}
          {/* ★仅排队中可取消(2026-08-03 用户实测):RUNNING 的渲染停不下来,后端也已收窄为 409。
              改前 PENDING|RUNNING 都给按钮 → 用户在生成中点取消看到「成功」,图照常出。 */}
          {task?.status === "PENDING" && (
            <Button
              variant="outline"
              size="sm"
              disabled={busy || !task}
              onClick={handleCancel}
              className="border-destructive/40 text-destructive hover:bg-destructive/10"
            >
              <XCircle className="mr-1 h-3 w-3" />
              取消生成
            </Button>
          )}
          {(isFailed || isPartial) && (
            <Button
              variant="outline"
              size="sm"
              disabled={busy || !task}
              onClick={handleRetry}
              className="border-primary/40 text-primary hover:bg-primary/10"
            >
              <RefreshCw className="mr-1 h-3 w-3" />
              {t("taskDetail.retryAll")}
            </Button>
          )}
          {!isWaiting && task && !isLongImage && selectedAsset && (
            <NominateToKbButton
              taskId={task.id}
              asset={selectedAsset}
              disabled={editorSavePending}
            />
          )}
          <span className="mx-0.5 h-5 w-px bg-border/60" aria-hidden />
          <Button
            variant="ghost"
            size="sm"
            disabled={busy || !task}
            onClick={handleDelete}
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="mr-1 h-3 w-3" />
            {t("taskDetail.deleteBtn")}
          </Button>
        </div>
      </header>

      {/* ★超时说人话 + 给出路(W3)。改前只有一句「任务轮询超时, 请刷新页面」——
          它既没说清发生了什么(前端不再问了 ≠ 后端停了),也没说已经出好的图还在不在。
          出图最长 5 分钟就会被 reaper 判死,所以这时最可能的真相是「卡住了」。 */}
      {timedOut && (
        <div className="flex flex-col gap-2 rounded-lg border border-warning/40 bg-warning/5 p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-2 text-warning">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="text-foreground/85">
              等太久了,可能是卡住了。
              {progress.done > 0
                ? `已经出好的 ${progress.done} 张仍在下面,可以先用。`
                : "刷新看看,或者取消这一单重新发。"}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={restart}>
              <RefreshCw className="h-3.5 w-3.5" />
              刷新看看
            </Button>
            {/* 卡在排队 → 还能撤;已在生成(RUNNING)→ 取消停不下来,出路只有刷新/等 reaper 判死重试 */}
            {task?.status === "PENDING" && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/10"
                disabled={busy}
                onClick={handleCancel}
              >
                <XCircle className="h-3.5 w-3.5" />
                取消这一单
              </Button>
            )}
          </div>
        </div>
      )}

      {/* 1017 算法备案:平台人工驳回的图租户侧已下架,后端把说明写进 errorMessage(任务仍是 SUCCESS) */}
      {!isFailed && task?.errorMessage?.includes("未通过平台内容审核") && (
        <Card data-testid="poster-review-rejected" className="border-warning/50 bg-warning/5">
          <CardContent className="flex items-start gap-3 p-4">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <div className="text-sm text-foreground/80">{task.errorMessage}</div>
          </CardContent>
        </Card>
      )}

      {isFailed && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:gap-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-destructive/15 text-destructive">
              <AlertCircle className="h-4.5 w-4.5" />
            </div>
            <div className="flex-1 space-y-1">
              <div className="text-sm font-semibold text-destructive">
                {t("taskDetail.failedTitle")}
              </div>
              <div className="text-sm text-foreground/80">
                {maskVendor(task?.errorMessage) ?? t("taskDetail.failedDefault")}
              </div>
              <div className="text-xs text-muted-foreground">{t("taskDetail.failedAdvice")}</div>
            </div>
            <Button
              variant="default"
              disabled={busy || !task}
              onClick={handleRetry}
              className="shrink-0 gap-1 bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              {t("taskDetail.retryNow")}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ★取消态(W3):改前取消完只剩一个「已取消」badge,没人告诉用户
          「已经出好并且已经扣过费的那几张还在不在」。 */}
      {task?.status === "CANCELLED" && (
        <Card className="border-border/70 bg-muted/30">
          <CardContent className="flex items-start gap-3 p-3 text-sm">
            <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="text-foreground/85">
              这一单已取消。
              {progress.done > 0
                ? `已经出好的 ${progress.done} 张保留在下面,可以正常使用。`
                : "没有产出,不计费。"}
            </div>
          </CardContent>
        </Card>
      )}

      {isPartial && (
        <Card className="border-warning/50 bg-warning/5">
          <CardContent className="flex items-start gap-3 p-3 text-sm text-warning">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              {blockTally
                ? t("taskDetail.partialBodyLongImage", {
                    fail: blockTally.failed,
                    total: blockTally.total,
                  })
                : t("taskDetail.partialBody", {
                    fail: task?.failedCount,
                    total: task?.requestedCount,
                  })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 止血②(2026-08-23 mzb 真单):账本披露 —— rejected 含真料时一行警示。
          不拦成功、不改任务态;用户看到 SUCCESS 的同时知道哪些内容没上图。 */}
      {task?.ledgerWarning?.message && (
        <Card className="border-warning/50 bg-warning/5" data-testid="ledger-warning">
          <CardContent className="flex items-start gap-3 p-3 text-sm text-warning">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>{task.ledgerWarning.message}</div>
          </CardContent>
        </Card>
      )}

      {/* ── 主画面区:五形态同一副骨架 Stage | Dock(2026-08-07 版面重构)──────────
          ★高度上限**不看兄弟节点**,只看视口:`h-[calc(100dvh-13rem)]`。
            13rem ≈ 顶栏 56 + main padding 48 + 返回 28 + 页头 64(浏览器实测)。
            用固定公式而不是「flex 抢剩余高度」,是因为上面那几条横幅(超时/失败/部分失败/
            已取消)是条件渲染的 —— 抢剩余高度会让「出了状况的那些页」把看图台压成一条缝,
            而那几页恰恰更需要看清图。横幅出现时页面自己往下长,看图台尺寸不变。
          ★`min-h-[30rem]` 兜底:矮屏(<800)上宁可让页面滚,也不把图压没。 */}
      <div className="lg:h-[calc(100dvh-13rem)] lg:min-h-[30rem]">
        {shape === "LONG_IMAGE" ? (
          <div className="grid h-full min-h-0 gap-4 lg:grid-cols-[minmax(0,1fr)_400px]">
            {/* 长图整图走专属看图台(海报画布编辑器对长图既无意义又会渲成数千 px 高) */}
            {task && longImageFull && (
              <LongImageResultPanel
                taskId={task.id}
                asset={longImageFull}
                hasSegments={assets.some((a) => a.segment === true)}
                screenSegments={assets.some((a) => a.segment === true && isScreenSegment(a))}
                jump={longImageJump}
                onTopRatio={setLongImageRatio}
                /* ★整图级「挂 logo/二维码」按钮已下线(0911 老板拍板):它与分屏卡
                   「画面调整」里的挂标是同一件事,但选不了位置 —— 弱能力入口留着只
                   制造「点哪个」的困惑。成品页挂标唯一口=任意一屏「画面调整」
                   (可选位置可预览,restitch 后整图生效)。出图前的整图 logo 槽
                   (备料页,CanvasIntakeBox)不受影响。 */
              />
            )}
            {/* 整图没交付(stitch/回调失败,只剩分段)。老版这里落进通用网格渲一排「等待中」骨架 ——
              任务已经终态了还在转圈,等不到的。分两种态说清楚:还在跑=等待面板,终态=明确说明 + 重试。
              ★等待面板(2026-08-03):此前只有一行「长图拼接中,稍候…」转圈 —— 长图被 W2 槽位模型
              绕行后等待期什么都看不到。改成按本机草稿快照搭「即将出的那张」骨架墙(块名/引擎标真
              来自规划,零编造),快照对不上落通用骨架。 */}
            {task &&
              !longImageFull &&
              (task.status === "PENDING" || task.status === "RUNNING" ? (
                <LongImageGeneratingPanel
                  taskId={task.id}
                  status={task.status}
                  createdAt={task.createdAt}
                  queueInfo={task.queueInfo}
                />
              ) : (
                <Card className="fx-skeleton">
                  <CardContent className="flex flex-col items-center gap-2 p-6 text-center">
                    <AlertTriangle className="h-5 w-5 text-warning" />
                    <span className="text-sm font-medium">整图未交付</span>
                    <span className="text-xs text-muted-foreground">
                      分段已生成(右侧可逐块查看),但整图拼接没成功。可以重试整图。
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-1 gap-1.5"
                      disabled={busy}
                      onClick={handleRetry}
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      {t("taskDetail.retryAll")}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            {/* Dock:品牌 + 分段面板 = 长图版的「画面调整」。改前分段面板在页面 y=1046
                (首屏底下 146px),用户得先滚过一张 7582px 高的取景框才够得着。 */}
            {/* Dock:分段面板 = 长图版的「画面调整」。整图级挂标已移进整图工具条
                (headerExtra),这里不再摆孤立行 —— 让分屏卡与左侧整图顶对齐。 */}
            <div className="flex min-h-0 flex-col gap-3">
              {taskId != null && (
                <LongImageSegmentsPanel
                  taskId={taskId}
                  assets={assets}
                  onRefresh={refresh}
                  dense
                  activeBlockId={activeSegmentId}
                  onAdjust={setAdjusting}
                  editSpec={longImageSpec.raw}
                  {...(longImageOffsets
                    ? {
                        onJumpToBlock: (blockId: string) => {
                          const ratio = longImageOffsets.ratioOf.get(blockId);
                          if (ratio == null) return;
                          setLongImageJump((prev) => ({ ratio, nonce: (prev?.nonce ?? 0) + 1 }));
                        },
                      }
                    : {})}
                />
              )}
              {/* ★原文对照(LI30-C2,0910 拍板①「事实不丢+成品页留对照」):备料原文由
                  接续侧随任务落库(longImage.sourceText→userPrompt 透出)。成图文案是
                  它的图面改写 —— 用户想核「有没有丢我的东西」,在这就能逐段对。老任务
                  没存 → 整个区不出现。 */}
              {task?.userPrompt && (
                <details
                  data-testid="li-source-text"
                  className="rounded-lg border border-border/60 bg-background/40 p-3 text-xs"
                >
                  <summary className="cursor-pointer select-none font-medium text-foreground">
                    原文对照
                    <span className="ml-2 text-2xs font-normal text-muted-foreground">
                      你的备料原文 —— 成图文案由它改写,事实与数字逐条对得上
                    </span>
                  </summary>
                  <p className="mt-2 max-h-64 overflow-y-auto whitespace-pre-wrap leading-relaxed text-muted-foreground">
                    {task.userPrompt}
                  </p>
                </details>
              )}
            </div>
            {/* 「改这一块」全屏编辑器(2026-08-08):画面调整 / 改内容 / AI 改图 / AI 重画 */}
            {taskId != null && adjusting && (
              <SegmentEditorDialog
                open
                onOpenChange={(o) => !o && setAdjusting(null)}
                taskId={taskId}
                asset={adjusting}
                blockName={adjusting.blockName || adjusting.styleTag || "这一块"}
                blockSpec={
                  adjusting.blockId
                    ? (longImageSpec.blockById.get(adjusting.blockId) ?? null)
                    : null
                }
                regenCredits={blockRegenQuote.credits}
                onSaved={() => {
                  setAdjusting(null);
                  restart();
                }}
              />
            )}
          </div>
        ) : isGenerating ? (
          /* ── 等待态主体(W2)────────────────────────────────────────────────
             改前这里没有分支:没有 selectedAsset 就只渲一条成品条,成品页最大的那块版面
             在等待时是一片纯背景。现在等待态有自己的主体,且**占住同一块位置** ——
             出齐后交叉到 AssetEditor 时,主位与缩略条落在同样的地方,不用重新找东西。 */
          <div className="h-full overflow-y-auto">
            <GeneratingPanel
              progress={progress}
              submittedAt={task?.submittedAt}
              // 1006:对客只回显用户原文 —— rawPrompt 带提交时拼的【画幅构图】【产品】等系统段
              prompt={task ? promptForRecreate(task) : null}
              selectedSeq={pickedByUser ? selectedAsset?.seq : undefined}
              onPick={(slot) => {
                if (!slot.asset) return;
                setSelectedAssetId(slot.asset.id);
                setPickedByUser(true);
              }}
            />
          </div>
        ) : task && selectedAsset && templatesQ.data?.templates.length ? (
          <AssetEditor
            task={task}
            asset={selectedAsset}
            allAssets={displayAssets}
            templates={templatesQ.data.templates}
            brand={editorBrand}
            onSelectVersion={setSelectedAssetId}
            onRefresh={refresh}
            onRestart={restart}
            onSavingChange={setEditorSavePending}
            stageBadge={slotStageBadge(selectedAsset ? slotOf.get(selectedAsset.seq) : undefined)}
            /* W2:成品条随编辑器一起挂 —— 切图零滚动(老版在页面最底部);
               2026-08-07 它落在操作台顶部而不是画布下方,理由见 AssetEditor 的 `tray` 注释 */
            tray={resultStrip}
          />
        ) : (
          /* 还没出图 / 全失败:没有可编辑的选中图,成品条独立渲染(骨架按目标比例) */
          <div className="h-full overflow-y-auto">{resultStrip}</div>
        )}
      </div>

      {/* I2V-PC-1:「让这张图动起来」轻确认层 —— 挂在分支外,单图/组图/长图选中块都能用 */}
      <AnimateImageDialog trigger={animateTrigger} onClose={() => setAnimateTrigger(null)} />

      {shape === "GROUP" && (
        <GroupCarouselPreview
          open={carouselOpen}
          onOpenChange={setCarouselOpen}
          items={stripAssets.map((a) => ({
            seq: a.seq,
            url: a.thumbUrl ?? a.baseUrl,
            caption: slotCaption(slotOf.get(a.seq), a),
            failed: a.status === "FAILED",
          }))}
          startIndex={Math.max(
            stripAssets.findIndex((a) => a.id === selectedAssetId),
            0
          )}
        />
      )}

      {/* W2:溯源信息降级到页尾折叠区,默认收起只留一行摘要 */}
      {task && (
        <TaskSettingsFold
          task={task}
          longImage={shape === "LONG_IMAGE" ? longImageSpec.raw : null}
          onRecreate={() =>
            // 「用此提示词重新创作」按形态分流(2026-08-11 报障):长图此前无条件跳
            // /ai-haibao/new(海报自由创作),用户从长图出发落到海报舱。长图走搭建器,
            // 种子用与「回搭建器改这一稿」同一条既有通道(longImageEditSpec)——
            // 搭建器不认 prefillPrompt,别发它读不懂的字段。
            shape === "LONG_IMAGE"
              ? navigate("/ai-haibao/detail-long", {
                  state: {
                    longImageEditTask: taskId,
                    longImageEditSpec: longImageSpec.raw,
                  },
                })
              : navigate("/ai-haibao/new", {
                  state: {
                    // ★回填用户原文,不是拼装串(2026-09-01 爱民):rawPrompt 灌回文案框
                    //   再提交,【画幅构图】段会再叠一层(774→804 那单出现两遍)。
                    prefillPrompt: promptForRecreate(task) || undefined,
                    prefillNote: task.notes?.trim() || undefined,
                    // ★把**这一单是什么形态**一起带过去(琳琳 0810,图 0810_4/4:
                    //   「全渠道套图做完继续做第二张,跳转的是商品主图」)。
                    //   此前只带文案 → 落到创作页的默认形态(单图/商品主图),
                    //   用户以为自己回到了套图,出来的却是一张主图,而且**出图之前看不出来**。
                    //   走的是既有的 scenePreset handoff 通路(PosterModeBar 同一条),不新造。
                    //   套图连原来那几个比例一起带 —— 「再做一张」是再做**同一种**,
                    //   默认三比例会把他上次选的比例悄悄换掉。
                    scenePreset: recreatePreset(shape, task.ratios),
                  },
                })
          }
        />
      )}
    </div>
  );
}

/**
 * 「用此提示词重新创作」要落回**同一种形态**(琳琳 0810)。
 *
 * 与 `PosterModeBar.PRESET_OF` 同一套 scenePreset 形状 —— 那边是「换一张卡」,
 * 这边是「再做一张同样的」,共用同一条 handoff 通路,别各造一份。
 * 未知形态返回 undefined:不给 preset = 保持创作页默认行为(零回归)。
 */
export function recreatePreset(
  shape: string,
  ratios?: string[] | null
): Record<string, unknown> | undefined {
  if (shape === "GROUP") return { kind: "poster_group", groupPoints: [] };
  if (shape === "RATIO_SET") {
    // ★必须先按 `PRESET_RATIOS` 过一遍再去重 —— `parseScenePreset` 对 poster_multiratio 是
    //   **整包丢弃**:数组里只要有一个不在这 5 档里,或去重后不足 2 个,它就 return null,
    //   于是 preset 等于没送,创作页照样落回默认的商品主图 —— 正是这条要修的 bug 本身,
    //   而且**一声不吭**。真机验收当场逮到(库里那单是 9:16 + 4:3,4:3 不在白名单)。
    //   兜底给默认三件套而不是 undefined:已经知道原单是套图,退到「套图 + 默认比例」
    //   永远好过退到「商品主图」。
    const rs = [...new Set((ratios ?? []).filter((r): r is PresetRatio => isPresetRatio(r)))];
    return {
      kind: "poster_multiratio",
      ratios: rs.length >= 2 ? rs : ["9:16", "1:1", "16:9"],
    };
  }
  if (shape === "SINGLE") return { kind: "poster_single" };
  return undefined;
}

function StatusBadge({ status, label }: { status: string; label?: string }) {
  // 色调(cls)收口到 lib/taskStatus(海报/视频共用);详情页保留英文 pill + 图标的本地设计。
  const ICON: Record<string, typeof CheckCircle2> = {
    PENDING: Loader2,
    RUNNING: Loader2,
    SUCCESS: CheckCircle2,
    PARTIAL: AlertTriangle,
    FAILED: AlertCircle,
  };
  const Icon = ICON[status];
  const spin = status === "PENDING" || status === "RUNNING";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-2xs font-semibold",
        taskStatusBorderedBadgeClass(status)
      )}
    >
      {Icon && <Icon className={cn("h-3 w-3", spin && "animate-spin")} />}
      {/* ★等待态说「正在出图」而不是「处理中」——但只在这一页覆盖:
          `taskStatusLabel` 是海报/视频**列表页共用**的,改它会波及视频舱。 */}
      {label ?? taskStatusLabel(status)}
    </span>
  );
}
